import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { SignedIn, useAuth, useClerk } from '@clerk/clerk-react';
import { api } from '../utils/api';
import { useMeRole } from '../hooks/useMeRole';
import { buildFacebookProjection, TVSA } from '../utils/tvsaProjections';
import { resolveMediaUrl } from '../utils/mediaUrl';

/**
 * Market owner dashboard:
 * - /owner → Vendors (invite, search, manage)
 * - /owner/sales → Actual sales day / week / month
 * - /owner/projected → Facebook projection (1 sale/vendor/open day + parking boost)
 */
export default function OwnerDashboard() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const navigate = useNavigate();
  const location = useLocation();
  const { isOwner, loading: roleLoading } = useMeRole();

  const activeTab = location.pathname.startsWith('/owner/projected')
    ? 'projected'
    : location.pathname.startsWith('/owner/sales')
      ? 'sales'
      : 'vendors';

  const [reportsPeriod, setReportsPeriod] = useState('day');
  const [projPeriod, setProjPeriod] = useState('day');
  const [ownerReports, setOwnerReports] = useState(null);
  const [vendorInfo, setVendorInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [vendorsLoading, setVendorsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inviteMsg, setInviteMsg] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [vendorQuery, setVendorQuery] = useState('');
  const [marketImageUrl, setMarketImageUrl] = useState(null);
  const [uploadingMarketImage, setUploadingMarketImage] = useState(false);
  const marketImageInputRef = useRef(null);

  useEffect(() => {
    if (!roleLoading && !isOwner) {
      navigate('/profile', { replace: true });
    }
  }, [isOwner, roleLoading, navigate]);

  useEffect(() => {
    if (activeTab !== 'sales' || !isOwner) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await api(`/api/markets/owner/reports?period=${reportsPeriod}`, { getToken });
        if (!res.ok) {
          if (res.status === 403) {
            if (!cancelled) navigate('/', { replace: true });
            return;
          }
          const data = await res.json().catch(() => ({}));
          throw new Error(data.error || `Error ${res.status}`);
        }
        const data = await res.json();
        if (!cancelled) setOwnerReports(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [getToken, reportsPeriod, navigate, activeTab, isOwner]);

  const loadVendors = useCallback(async () => {
    setVendorsLoading(true);
    try {
      const res = await api('/api/markets/owner/vendors', { getToken });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
      setVendorInfo(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setVendorsLoading(false);
    }
  }, [getToken]);

  const loadMarket = useCallback(async () => {
    try {
      const res = await api('/api/markets/owner', { getToken });
      if (!res.ok) return;
      const data = await res.json();
      setMarketImageUrl(data.imageUrl || null);
      if (data.name) {
        setVendorInfo((prev) => (prev ? { ...prev, marketName: data.name } : prev));
      }
    } catch {
      // non-blocking
    }
  }, [getToken]);

  useEffect(() => {
    if (isOwner && (activeTab === 'vendors' || activeTab === 'projected')) loadVendors();
  }, [isOwner, loadVendors, activeTab]);

  useEffect(() => {
    if (isOwner && activeTab === 'vendors') loadMarket();
  }, [isOwner, activeTab, loadMarket]);

  const handleMarketImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingMarketImage(true);
    setError(null);
    setInviteMsg(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const up = await api('/api/images/upload', {
        getToken,
        method: 'POST',
        body: formData,
      });
      if (!up.ok) {
        const data = await up.json().catch(() => ({}));
        throw new Error(data.error || `Upload failed (${up.status})`);
      }
      const uploaded = await up.json();
      let url = uploaded.url || uploaded.imageUrl;
      if (!url) throw new Error('Upload returned no URL');
      try {
        const parsed = new URL(url, window.location.origin);
        if (parsed.pathname.startsWith('/public/') || parsed.pathname.startsWith('/uploads/')) {
          url = parsed.pathname;
        }
      } catch {
        // keep original
      }

      const res = await api('/api/markets/owner', {
        getToken,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: url }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || data.details || `Save failed (${res.status})`);
      }
      setMarketImageUrl(url);
      setInviteMsg('Market image saved. It shows on the home tab.');
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingMarketImage(false);
      if (marketImageInputRef.current) marketImageInputRef.current.value = '';
    }
  };
  const filteredVendors = useMemo(() => {
    const list = vendorInfo?.vendors ?? [];
    const q = vendorQuery.trim().toLowerCase();
    if (!q) return list;
    return list.filter((v) => {
      const name = (v.storeName || '').toLowerCase();
      const booth = (v.locationText || '').toLowerCase();
      const phone = (v.phone || '').toLowerCase();
      const id = String(v.id);
      return name.includes(q) || booth.includes(q) || phone.includes(q) || id.includes(q);
    });
  }, [vendorInfo, vendorQuery]);

  const facebookProjection = useMemo(
    () => buildFacebookProjection(projPeriod, TVSA.averageDealers),
    [projPeriod]
  );

  const handleCopyInvite = async () => {
    setInviteMsg(null);
    setError(null);
    const marketId = vendorInfo?.marketId ?? ownerReports?.marketId;
    if (!marketId) {
      setError('Market not loaded yet.');
      return;
    }
    try {
      const res = await api('/api/invites', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fleaMarketId: marketId, maxUses: 0, expiresInDays: 30 }),
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed to create invite (${res.status})`);
      const url = `${window.location.origin}${data.invitePath || `/invite/${data.token}`}`;
      await navigator.clipboard.writeText(url);
      setInviteMsg('Onboarding invite link copied. Send it to a vendor.');
      setTimeout(() => setInviteMsg(null), 3000);
    } catch (err) {
      setError(err.message || 'Could not create invite.');
    }
  };

  const handleRemoveVendor = async (vendorId, name) => {
    if (!window.confirm(`Remove vendor "${name || vendorId}" from your market? Their items will be hidden.`)) return;
    setRemovingId(vendorId);
    setError(null);
    try {
      const res = await api(`/api/markets/owner/vendors/${vendorId}`, {
        method: 'DELETE',
        getToken,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || `Failed (${res.status})`);
      await loadVendors();
    } catch (err) {
      setError(err.message);
    } finally {
      setRemovingId(null);
    }
  };

  const periodLabel =
    ownerReports?.periodLabel ||
    (reportsPeriod === 'week' ? 'This weekend (Sat–Sun)' : reportsPeriod === 'month' ? 'This month (Sat–Sun)' : 'Last open day');
  const projPeriodLabel =
    projPeriod === 'week'
      ? '7 selling days (pickup Sat–Sun only)'
      : projPeriod === 'month'
        ? `${facebookProjection.sellingDays} calendar days (pickup on ${facebookProjection.pickupDays} open days)`
        : '1 selling day (pickup on an open day)';

  const title =
    activeTab === 'vendors' ? 'Vendors' : activeTab === 'projected' ? 'Facebook projected' : 'Sales';

  if (roleLoading) {
    return (
      <SignedIn>
        <div className="flex items-center justify-center py-16">
          <p className="text-2xl text-slate-500 dark:text-slate-400">Loading…</p>
        </div>
      </SignedIn>
    );
  }

  if (!isOwner) return null;

  return (
    <SignedIn>
      <div className="flex flex-col gap-6 max-w-screen-md mx-auto w-full px-4 -mt-2 pb-4">
        <div className="flex justify-end -mr-4 md:-mr-8">
          <button
            type="button"
            onClick={() => signOut({ redirectUrl: '/' })}
            className="text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100 underline"
          >
            Sign out
          </button>
        </div>

        <header className="text-center">
          <h1 className="text-3xl font-semibold">{title}</h1>
          {(ownerReports?.marketName || vendorInfo?.marketName) && (
            <p className="text-lg text-slate-600 dark:text-slate-400 mt-1">
              {ownerReports?.marketName || vendorInfo?.marketName}
            </p>
          )}
        </header>

        {error && (
          <div className="rounded-xl bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 p-4">
            {error}
          </div>
        )}
        {inviteMsg && activeTab === 'vendors' && (
          <div className="rounded-xl bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200 p-4">
            {inviteMsg}
          </div>
        )}

        {activeTab === 'vendors' && (
          <section className="flex flex-col gap-4">
            <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 flex flex-col gap-3">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-slate-100">
                Flea market tab image
              </h2>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                Shown on the home page and market page for customers.
              </p>
              {marketImageUrl && (
                <div className="aspect-[21/9] w-full rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-700">
                  <img
                    src={resolveMediaUrl(marketImageUrl) || marketImageUrl}
                    alt="Market"
                    className="w-full h-full object-cover"
                  />
                </div>
              )}
              <input
                ref={marketImageInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleMarketImageUpload}
              />
              <button
                type="button"
                disabled={uploadingMarketImage}
                onClick={() => marketImageInputRef.current?.click()}
                className="h-12 px-4 rounded-xl font-semibold bg-slate-800 text-white hover:bg-slate-900 disabled:opacity-50 self-start"
              >
                {uploadingMarketImage ? 'Uploading…' : marketImageUrl ? 'Replace market image' : 'Upload market image'}
              </button>
            </div>

            {vendorsLoading ? (
              <p className="text-slate-500 text-center py-8">Loading vendors…</p>
            ) : vendorInfo ? (
              <>
                <p className="text-slate-600 dark:text-slate-400 text-center">
                  Slots: {vendorInfo.currentVendorCount} / {vendorInfo.vendorLimit}
                  {' '}({vendorInfo.slotsRemaining} remaining)
                </p>
                <button
                  type="button"
                  onClick={handleCopyInvite}
                  disabled={vendorInfo.slotsRemaining <= 0}
                  className="h-12 rounded-xl font-semibold bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
                >
                  Copy onboarding invite link
                </button>

                <label className="flex flex-col gap-2">
                  <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Search vendors</span>
                  <input
                    type="search"
                    value={vendorQuery}
                    onChange={(e) => setVendorQuery(e.target.value)}
                    placeholder="Store name, booth #, or phone"
                    className="h-12 w-full rounded-xl border-2 border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-4 text-lg focus:outline-none focus:border-indigo-500"
                  />
                </label>

                {filteredVendors.length > 0 ? (
                  <ul className="flex flex-col gap-2">
                    {filteredVendors.map((v) => (
                      <li
                        key={v.id}
                        className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-3"
                      >
                        <div className="min-w-0">
                          <div className="font-medium truncate">{v.storeName || `Vendor #${v.id}`}</div>
                          <div className="text-sm text-slate-500">
                            {v.locationText ? `Booth ${v.locationText}` : 'No booth set'}
                            {v.phone ? ` · ${v.phone}` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveVendor(v.id, v.storeName)}
                          disabled={removingId === v.id}
                          className="shrink-0 text-sm font-medium text-red-600 hover:text-red-800 disabled:opacity-50"
                        >
                          {removingId === v.id ? 'Removing…' : 'Remove'}
                        </button>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-500 text-center py-6">
                    {vendorQuery.trim()
                      ? `No vendors match “${vendorQuery.trim()}”.`
                      : 'No booth vendors yet. Send an invite link to add one.'}
                  </p>
                )}
              </>
            ) : null}
          </section>
        )}
        {activeTab === 'sales' && (
          <section className="flex flex-col gap-4">
            <p className="text-slate-600 dark:text-slate-400 text-center">
              In-person market: Sat–Sun only. Day = last open day, week = that Sat+Sun, month = all open days this month.
              Benchmark ~${TVSA.avgVendorGrossPerOpenDayUsd}/dealer/open day (industry $200–$500) × ~{TVSA.averageDealers.toLocaleString()} dealers (TVSA FAQ).
              Your cut is 2% of gross.
            </p>
            <div className="flex gap-2 justify-center">
              {['day', 'week', 'month'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setReportsPeriod(p)}
                  className={`px-5 py-2.5 rounded-lg capitalize font-semibold transition-all ${
                    reportsPeriod === p
                      ? 'bg-indigo-600 text-white shadow-lg ring-2 ring-indigo-400 ring-offset-2 dark:ring-offset-slate-900'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 shadow-none ring-0'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <p className="text-center text-sm font-medium text-slate-500">{periodLabel}</p>
            {loading ? (
              <div className="text-slate-500 dark:text-slate-400 py-8 text-center">Loading reports…</div>
            ) : ownerReports ? (
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <div className="text-sm text-blue-700 dark:text-blue-300">Dealer-days / sales rows</div>
                    <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                      {ownerReports.totalSalesCount.toLocaleString()}
                    </div>
                  </div>
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                    <div className="text-sm text-green-700 dark:text-green-300">Gross Sales</div>
                    <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                      ${(ownerReports.totalGross ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                    <div className="text-sm text-purple-700 dark:text-purple-300">Profit (2%)</div>
                    <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                      ${((ownerReports.totalGross ?? 0) * 0.02).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </div>
                  </div>
                </div>
                {ownerReports.reports && ownerReports.reports.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                      <thead>
                        <tr className="border-b border-slate-300 dark:border-slate-600">
                          <th className="p-3 text-lg font-semibold">Open day</th>
                          <th className="p-3 text-lg font-semibold">Sales</th>
                          <th className="p-3 text-lg font-semibold">Gross</th>
                          <th className="p-3 text-lg font-semibold">Profit (2%)</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ownerReports.reports.map((report, idx) => (
                          <tr key={idx} className="border-b border-slate-200 dark:border-slate-700">
                            <td className="p-3">{report.period}</td>
                            <td className="p-3">{report.salesCount.toLocaleString()}</td>
                            <td className="p-3 font-medium">
                              ${(report.totalGross ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="p-3 font-medium text-purple-600 dark:text-purple-400">
                              ${((report.totalGross ?? 0) * 0.02).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">No sales for {periodLabel.toLowerCase()}.</div>
                )}
              </div>
            ) : null}
          </section>
        )}

        {activeTab === 'projected' && (
          <section className="flex flex-col gap-4">
            <p className="text-slate-600 dark:text-slate-400 text-center">
              Facebook / online: 1 sale per dealer per calendar day
              ({TVSA.averageDealers.toLocaleString()} dealers → {TVSA.averageDealers.toLocaleString()}/day,{' '}
              {(TVSA.averageDealers * 7).toLocaleString()}/week). Selling 7 days; pickup only Sat–Sun.
              Parking <strong>${TVSA.parkingFeeUsd}</strong> per buyer at pickup (admission free).
            </p>
            <div className="flex gap-2 justify-center">
              {['day', 'week', 'month'].map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setProjPeriod(p)}
                  className={`px-5 py-2.5 rounded-lg capitalize font-semibold transition-all ${
                    projPeriod === p
                      ? 'bg-indigo-600 text-white shadow-lg ring-2 ring-indigo-400 ring-offset-2 dark:ring-offset-slate-900'
                      : 'bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-300 shadow-none ring-0'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
            <p className="text-center text-sm font-medium text-slate-500">{projPeriodLabel}</p>
            <p className="text-center text-xs text-slate-500">
              {facebookProjection.vendors.toLocaleString()} dealers × {facebookProjection.sellingDays} selling day
              {facebookProjection.sellingDays === 1 ? '' : 's'}
              {' · '}
              pickup windows: {facebookProjection.pickupDays}
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <div className="text-sm text-blue-700 dark:text-blue-300">Projected sales</div>
                <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                  {facebookProjection.salesCount.toLocaleString()}
                </div>
              </div>
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <div className="text-sm text-green-700 dark:text-green-300">
                  Merchandise (${TVSA.avgOnlineSaleUsd} avg)
                </div>
                <div className="text-2xl font-bold text-green-900 dark:text-green-100">
                  ${facebookProjection.merchandiseGross.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 p-4 rounded-lg">
                <div className="text-sm text-amber-800 dark:text-amber-200">
                  Parking boost (${TVSA.parkingFeeUsd} × buyers at pickup)
                </div>
                <div className="text-2xl font-bold text-amber-900 dark:text-amber-100">
                  ${facebookProjection.parkingBoost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg">
                <div className="text-sm text-purple-700 dark:text-purple-300">Owner 2% + parking</div>
                <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                  ${facebookProjection.ownerTotalWithParking.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                <div className="text-xs text-purple-600 dark:text-purple-300 mt-1">
                  2% goods ${facebookProjection.ownerMerchandiseCut.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  {' '}+ parking ${facebookProjection.parkingBoost.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <p className="text-xs text-center text-slate-500">{TVSA.sourceNote}</p>
          </section>
        )}
      </div>
    </SignedIn>
  );
}
